import React, { type ReactElement, Suspense, useEffect, useMemo } from 'react';
import { type BottomTabBarProps } from '@react-navigation/bottom-tabs';
import { BottomTabBarContainer, BottomTabBarItem } from 'shared/src/component';
import { useLogClickBottomTab } from './hooks/useLogClickBottomTab';
import {
  BadgeNotification,
  Flex,
  RemoteImage,
  useScreenSize,
} from '@banhala/ui-kit';
import { useMainTabs, useMyInfo } from 'shared/src/queries';
import type { MainTabItem } from 'shared/src/api';
import { useLocalStorageState } from '../../hooks';
import { convertToDayJs, getToday, isAndroid, isNotNil } from '../../utils';

const TAB_BAR_ICON_SIZE = 24;

// 마지막으로 알림 화면을 "열람한 시각"을 담는 네이티브 로컬 저장소 키.
// 플랫폼별로 네이티브가 사용하는 키를 그대로 재사용한다.
const NOTIFICATION_VIEWED_AT_KEY = isAndroid
  ? 'KEY_NOTIFICATION_ALL_LAST_NOTIFIED_AT'
  : 'lastFetchedOrderNotificationOpenedDate';

const VIEWED_AT_FORMAT = 'YYYY-MM-DD HH:mm:ss';

const RedDotComp = (props: {
  tab: MainTabItem;
  children: ReactElement;
  focused: boolean;
}) => {
  const { data: me } = useMyInfo();
  const [viewedAt, setViewedAt, hydrated] = useLocalStorageState<string>(
    NOTIFICATION_VIEWED_AT_KEY,
    '',
  );

  const showBadge = useMemo(() => {
    // 저장값 hydrate 완료 전에는 판단 보류 → 초기값('')로 인한 가짜 뱃지/열람처리 방지.
    if (!hydrated || props.tab.screen_name !== 'MY_PAGE') {
      return false;
    }

    const serverNotifiedAt = me?.last_notified_at;
    if (!isNotNil(serverNotifiedAt)) {
      return false;
    }

    // 열람 이력이 없으면 무조건 노출.
    if (viewedAt === '') {
      return true;
    }

    // 마지막 열람 시각이 서버 알림 시각보다 이전이면 새 알림이 있는 것.
    return convertToDayJs(viewedAt).isBefore(convertToDayJs(serverNotifiedAt));
  }, [hydrated, me?.last_notified_at, props.tab.screen_name, viewedAt]);

  useEffect(() => {
    // 마이페이지 탭이 포커스되고 뱃지가 떠 있으면 열람 처리(현재 시각 저장).
    if (props.focused && showBadge) {
      setViewedAt(getToday().format(VIEWED_AT_FORMAT));
    }
  }, [props.focused, showBadge, setViewedAt]);

  return (
    <Flex>
      {props.children}
      {showBadge && (
        <Flex position={'absolute'} right={0}>
          <BadgeNotification />
        </Flex>
      )}
    </Flex>
  );
};

const TabBarIcon: React.FC<{ tab: MainTabItem; focused: boolean }> = ({
  tab,
  focused,
}) => {
  const uri = focused ? tab.render.selected_icon : tab.render.unselected_icon;
  return (
    <RedDotComp tab={tab} focused={focused}>
      <RemoteImage
        uri={uri}
        width={TAB_BAR_ICON_SIZE}
        height={TAB_BAR_ICON_SIZE}
      />
    </RedDotComp>
  );
};

const BottomTabBarContent: React.FC<BottomTabBarProps> = props => {
  const { width: screenWidth } = useScreenSize();
  const logClickBottomTab = useLogClickBottomTab();
  const { data: mainTabs } = useMainTabs();

  // navigation route는 mainTabs 정의를 기반으로 등록되므로 인덱스 순서가 동일.
  // route.name → screen_name mapping 없이 서버 응답의 screen_name 을 그대로 사용.
  const currentScreen =
    mainTabs.item_list[props.state.index]?.screen_name ?? '';

  return (
    <BottomTabBarContainer width={screenWidth}>
      {mainTabs.item_list.flatMap((tab, index) => {
        const route = props.state.routes[index];
        if (!route) return [];

        const isFocused = props.state.index === index;
        const label = tab.name;
        const rawColor = isFocused
          ? tab.render.selected_color
          : tab.render.unselected_color;

        return [
          <BottomTabBarItem
            key={route.key}
            name={label}
            focused={isFocused}
            rawColor={rawColor}
            icon={<TabBarIcon tab={tab} focused={isFocused} />}
            onPress={() => {
              logClickBottomTab({ label, currentScreen });

              const event = props.navigation.emit({
                type: 'tabPress',
                target: route.key,
                canPreventDefault: true,
              });

              if (!isFocused && !event.defaultPrevented) {
                props.navigation.navigate(route.name, route.params);
              }
            }}
          />,
        ];
      })}
    </BottomTabBarContainer>
  );
};

export const BottomTabBar: React.FC<BottomTabBarProps> = props => {
  return (
    <Suspense fallback={null}>
      <BottomTabBarContent {...props} />
    </Suspense>
  );
};
